// TicketSupport: Staff generate for assigned bookings, resend, scan logs
import React, { useEffect, useState } from 'react';
import StaffLayout from '../../layouts/StaffLayout';
import { Box, Typography, Grid, Button, Paper, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Menu, MenuItem, Chip, Tooltip, IconButton, Divider } from '@mui/material';
import ReactCountryFlag from 'react-country-flag';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import api from '../../services/api';
import { useProfile } from '../../context/ProfileContext';

// Utility: country to flag emoji
const countryToFlag = (country) => {
  if (!country) return '';
  const c = String(country).trim().toLowerCase();
  if (c.includes('tanzania') || c === 'tz' || c === 'tan') return '🇹🇿';
  if (c.includes('kenya') || c === 'ke') return '🇰🇪';
  if (c.includes('uganda') || c === 'ug') return '🇺🇬';
  if (c.includes('china') || c === 'cn') return '🇨🇳';
  return '';
};

function BookerDetailModal({ open, onClose, booker }) {
  if (!booker) return null;
  const details = booker.bookingDetails || {};
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ bgcolor: (theme) => theme.palette.mode === 'dark' ? theme.palette.background.paper : '#f5f5fa', color: (theme) => theme.palette.primary.main, fontWeight: 700, fontSize: 20, textAlign: 'center' }}>Booker Details</DialogTitle>
      <DialogContent dividers sx={{ bgcolor: (theme) => theme.palette.mode === 'dark' ? theme.palette.background.default : '#fafbff', p: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" fontWeight={700} sx={{ color: (theme) => theme.palette.primary.main, fontSize: 18 }}>
              {details.bookerName || details.companyDetails?.name || 'N/A'}
            </Typography>
              {details.country && (
                <span title={details.country} style={{ marginLeft: 4 }}>
                  <ReactCountryFlag countryCode={details.country} svg style={{ width: '1.2em', height: '1.2em', verticalAlign: 'middle' }} />
                </span>
              )}
            {/* Verified ✔✔ */}
            {booker.status === 'approved' && <span title="Verified" style={{ color: '#1dbf73', marginLeft: 5, fontSize: 17, fontWeight: 700 }}>✔✔</span>}
            {/* Paid $ */}
            {(booker.paymentStatus === 'paid' || booker.paymentStatus === 'verified') && <span title="Paid" style={{ color: '#21c700', marginLeft: 5, fontSize: 17, fontWeight: 700 }}>$</span>}
          </Box>
          <Typography variant="body2" sx={{ color: (theme) => theme.palette.text.secondary, fontWeight: 500, fontSize: 15 }}>
            <b>Email:</b> {booker.userId?.email || details.email || 'N/A'}
          </Typography>
          <Typography variant="body2" sx={{ color: (theme) => theme.palette.text.primary, fontWeight: 500, fontSize: 15 }}>
            <b>Package:</b> {booker.packageId?.name || details.packageName || 'N/A'} | <b>Category:</b> {booker.packageId?.category || details.category || 'N/A'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 700, color: '#388e3c', fontSize: 15 }}>Status:</Typography>
            {booker.status === 'pending' ? (
              <span title="Pending" style={{ color: '#d32f2f', fontSize: 18 }}>✔</span>
            ) : booker.status === 'approved' ? (
              <span title="Approved" style={{ color: '#388e3c', fontSize: 18 }}>✔✔</span>
            ) : (
              <span style={{ fontWeight: 400 }}>{booker.status}</span>
            )}
            <Typography variant="body2" sx={{ fontWeight: 700, color: (theme) => theme.palette.primary.main, fontSize: 15, ml: 2 }}>Payment:</Typography>
            {booker.paymentStatus === 'pending' ? (
              <span title="Not Paid" style={{ color: '#d32f2f', fontSize: 18 }}>$</span>
            ) : (booker.paymentStatus === 'paid' || booker.paymentStatus === 'verified') ? (
              <span title="Paid" style={{ color: '#388e3c', fontSize: 18 }}>$</span>
            ) : (
              <span style={{ fontWeight: 400 }}>{booker.paymentStatus}</span>
            )}
          </Box>
        </Box>
        <Divider sx={{ my: 2, bgcolor: (theme) => theme.palette.divider }} />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 15, color: (theme) => theme.palette.text.primary }}>
              <b>Company:</b> {details.companyName || details.companyDetails?.name || 'N/A'}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 15, color: (theme) => theme.palette.text.primary }}>
              <b>Mobile:</b> {details.mobile || 'N/A'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {details.country && (
                <span title={details.country} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <ReactCountryFlag
                    countryCode={details.country}
                    svg
                    style={{ width: '2.5em', height: '2.5em', borderRadius: '5px', boxShadow: '0 0 4px #007bff', border: '2px solid #007bff', verticalAlign: 'middle', marginRight: 8 }}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#007bff', fontSize: 16 }}>{details.country}</Typography>
                </span>
              )}
              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 15, color: (theme) => theme.palette.text.primary }}><b>Country:</b> {details.country || 'N/A'}</Typography>
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 15, color: (theme) => theme.palette.text.primary }}>
              <b>City:</b> {details.city || 'N/A'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 15, color: (theme) => theme.palette.text.primary }}><b>Address:</b> {details.address || details.companyDetails?.address || 'N/A'}</Typography>
              <Tooltip title="Location">
                <span>
                  <IconButton size="small" sx={{ color: (theme) => theme.palette.primary.main, p: 0.5 }}>
                    <span role="img" aria-label="location" style={{ fontSize: 18 }}>📍</span>
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 15, color: (theme) => theme.palette.text.primary }}>
              <b>Title:</b> {details.title || 'N/A'}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 15, color: (theme) => theme.palette.text.primary }}>
              <b>Accommodation:</b> {details.accommodation ? 'Yes' : 'No'}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 15, color: (theme) => theme.palette.text.primary }}>
              <b>Airport Pickup:</b> {details.airportPickup ? 'Yes' : 'No'}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 15, color: (theme) => theme.palette.text.primary }}>
              <b>Date:</b> {booker.createdAt ? new Date(booker.createdAt).toLocaleString() : 'N/A'}
            </Typography>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function TicketSupport() {
  const [eligibleBookers, setEligibleBookers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(false);
  const [actionAnchorEl, setActionAnchorEl] = useState(null);
  const [actionBooking, setActionBooking] = useState(null);
  const { profile } = useProfile() || {};
  const user = profile || (typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('user') || '{}') : {});
  const [createOpen, setCreateOpen] = useState(false);
  const [createBooking, setCreateBooking] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailBooker, setDetailBooker] = useState(null);

  // helper: checks whether current user can act on an assigned booking
  const canActOn = (b) => {
    if (!b) return false;
    if (user?.role === 'superadmin' || user?.role === 'admin') return true;
    const assigned = b.assignedTo || b.assignedStaffId || b.staffId || b.assigned;
    const assignedId = typeof assigned === 'string' ? assigned : (assigned && (assigned._id || assigned.id));
    return assignedId && user?._id && String(assignedId) === String(user._id);
  };

  const countryToFlag = (country) => {
    if (!country) return '';
    const c = String(country).trim().toLowerCase();
    if (c.includes('tanzania') || c === 'tz' || c === 'tan') return '🇹🇿';
    if (c.includes('kenya') || c === 'ke') return '🇰🇪';
    if (c.includes('uganda') || c === 'ug') return '🇺🇬';
    if (c.includes('china') || c === 'cn') return '🇨🇳';
    return '';
  };

  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        // BADILISHA HAPA: tumia endpoint ya eligible-bookers kama admin
        const res = await api.get('/api/bookings/eligible-bookers');
        const data = res?.data;
        setEligibleBookers(Array.isArray(data) ? data : []);
        setError('');
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to fetch eligible bookers');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleGenerate = async (bookingId) => {
    setProcessing(true);
    try {
      await api.post(`/api/bookings/${bookingId}/generate-ticket`);
      alert('Ticket generated');
    } catch (e) {
      alert('Failed to generate ticket');
    } finally { setProcessing(false); }
  };

  const openCreateModal = (booking) => {
    setCreateBooking(booking);
    setCreateOpen(true);
  };

  const closeCreateModal = () => {
    setCreateOpen(false);
    setCreateBooking(null);
  };

  const submitCreateTicket = async () => {
    if (!createBooking) return;
    setProcessing(true);
    try {
      await api.post(`/api/bookings/${createBooking._id}/generate-ticket`, { method: 'manual' });
      alert('Ticket created');
      closeCreateModal();
    } catch (e) {
      alert('Failed to create ticket');
    } finally { setProcessing(false); }
  };

  const handleResend = async (bookingId) => {
    setProcessing(true);
    try {
      await api.post(`/api/bookings/${bookingId}/resend-ticket`);
      alert('Ticket resent');
    } catch (e) {
      alert('Failed to resend ticket');
    } finally { setProcessing(false); }
  };

  const viewScanLogs = async (bookingId) => {
    try {
      const res = await api.get(`/api/bookings/${bookingId}/scan-logs`);
      const data = res?.data;
      // display simple modal or console for now
      console.log('scan logs', data);
      alert(`Scan logs: ${JSON.stringify(data || []).slice(0, 300)}`);
    } catch (e) {
      alert('Failed to fetch scan logs');
    }
  };

  return (
    <StaffLayout>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Ticket Support
        </Typography>
        <Paper sx={{ p: 3, borderRadius: 3, mb: 3 }}>
          <Typography variant="body2" color="text.secondary" mb={2}>
            All eligible bookers (approved and paid, including speakers) are shown below. Staff can generate, resend, or view scan logs for tickets.
          </Typography>
          {error && <Typography color="error" mb={2}>{error}</Typography>}
          {loading ? (
            <Typography>Loading...</Typography>
          ) : (
            <Grid container spacing={3}>
              {(() => {
                const visible = eligibleBookers.filter(b => (b.bookingDetails?.bookerName || b.bookingDetails?.companyDetails?.name) && b.status === 'approved' && (b.paymentStatus === 'paid' || b.paymentStatus === 'verified'));
                if (!visible.length) return <Grid item xs={12}><Typography>No approved & paid bookers found.</Typography></Grid>;
                return visible.map((b) => {
                  const canAct = canActOn(b);
                  return (
                    <Grid item xs={12} sm={6} md={4} key={b._id}>
                      <Paper
                        sx={theme => ({
                          p: 2.2,
                          borderRadius: 3,
                          boxShadow: 3,
                          mb: 2,
                          background: theme.palette.mode === 'dark' ? '#23243a' : '#fcfcff',
                          border: `1.5px solid ${theme.palette.divider}`,
                          transition: 'background 0.2s',
                          '&:hover': {
                            boxShadow: 6,
                            background: theme.palette.mode === 'dark' ? '#28294a' : '#f5f7fa',
                          },
                        })}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="subtitle1" fontWeight={700} color="primary.main" sx={{ fontSize: 16.5 }}>
                                {b.bookingDetails?.bookerName || b.bookingDetails?.companyDetails?.name || 'N/A'}
                              </Typography>
                              {/* Flag using MUI Avatar-like style for visibility */}
                              {(() => {
                                const country = b.bookingDetails?.country || b.country || b.userId?.country || b.bookingDetails?.nationality || b.userId?.profile?.country;
                                const flag = countryToFlag(country);
                                return flag ? (
                                  <Box sx={{ display: 'flex', alignItems: 'center', ml: 0.5 }}>
                                    <span style={{ fontSize: 22, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 4px #0002', padding: '2px 6px', border: '1.5px solid #e0e0e0', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{flag}</span>
                                  </Box>
                                ) : null;
                              })()}
                              {/* Verified ✔✔ */}
                              {b.status === 'approved' && <span title="Verified" style={{ color: '#1dbf73', marginLeft: 5, fontSize: 15.5, fontWeight: 700, textShadow: '0 1px 2px #0001' }}>✔✔</span>}
                              {/* Paid $ */}
                              {(b.paymentStatus === 'paid' || b.paymentStatus === 'verified') && <span title="Paid" style={{ color: '#21c700', marginLeft: 5, fontSize: 15.5, fontWeight: 700, textShadow: '0 1px 2px #0001' }}>$</span>}
                            </Box>
                            <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontSize: 13.5, mt: 0.2 }}>
                              <b>Email:</b> {b.userId?.email || b.bookingDetails?.email || 'N/A'}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 0.2, fontSize: 13.2 }}>
                              <b>Package:</b> {b.packageId?.name || 'N/A'} | <b>Category:</b> {b.packageId?.category || 'N/A'}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 0.2, fontSize: 13.2 }}>
                              <b>Company:</b> {b.bookingDetails?.companyName || b.bookingDetails?.companyDetails?.name || 'N/A'}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 0.2, fontSize: 13.2 }}>
                              <b>Mobile:</b> {b.bookingDetails?.mobile || 'N/A'}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-end', minWidth: 38 }}>
                            <Tooltip title="Create Ticket">
                              <span>
                                <IconButton size="small" color="primary" disabled={!canAct || processing} onClick={() => openCreateModal(b)} sx={{ p: 0.7, mb: 0.2, bgcolor: theme => theme.palette.mode === 'dark' ? '#23243a' : '#f2f7fa', border: `1px solid ${theme => theme.palette.divider}` }}>
                                  <span role="img" aria-label="Create Ticket" style={{ fontSize: 15.5, display: 'block' }}>🎟️</span>
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Resend Ticket">
                              <span>
                                <IconButton size="small" color="secondary" disabled={!canAct || processing} onClick={() => handleResend(b._id)} sx={{ p: 0.7, mb: 0.2, bgcolor: theme => theme.palette.mode === 'dark' ? '#23243a' : '#f2f7fa', border: `1px solid ${theme => theme.palette.divider}` }}>
                                  <span role="img" aria-label="Resend Ticket" style={{ fontSize: 15.5, display: 'block' }}>📤</span>
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Scan Logs">
                              <IconButton size="small" onClick={() => viewScanLogs(b._id)} sx={{ p: 0.7, mb: 0.2, bgcolor: theme => theme.palette.mode === 'dark' ? '#23243a' : '#f2f7fa', border: `1px solid ${theme => theme.palette.divider}` }}>
                                <span role="img" aria-label="Scan Logs" style={{ fontSize: 15.5, display: 'block' }}>📝</span>
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Details">
                              <IconButton size="small" color="info" onClick={() => { setDetailBooker(b); setDetailOpen(true); }} sx={{ p: 0.7, mb: 0.2, bgcolor: theme => theme.palette.mode === 'dark' ? '#23243a' : '#f2f7fa', border: `1px solid ${theme => theme.palette.divider}` }}>
                                <span role="img" aria-label="Details" style={{ fontSize: 15.5, display: 'block' }}>ℹ️</span>
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      </Paper>
                    </Grid>
                  );
                });
              })()}
            </Grid>
          )}
          <Menu anchorEl={actionAnchorEl} open={Boolean(actionAnchorEl)} onClose={() => { setActionAnchorEl(null); setActionBooking(null); }}>
            <MenuItem onClick={() => { if (actionBooking) openCreateModal(actionBooking); setActionAnchorEl(null); }}>Create Ticket</MenuItem>
            <MenuItem onClick={() => { if (actionBooking) handleResend(actionBooking._id); setActionAnchorEl(null); }}>Resend Ticket</MenuItem>
            <MenuItem onClick={() => { if (actionBooking) viewScanLogs(actionBooking._id); setActionAnchorEl(null); }}>View Scan Logs</MenuItem>
            <MenuItem onClick={() => { if (actionBooking) { setDetailBooker(actionBooking); setDetailOpen(true); } setActionAnchorEl(null); }}>Details</MenuItem>
          </Menu>
        </Paper>
      {/* Create Ticket Dialog */}
      <Dialog open={createOpen} onClose={closeCreateModal}>
        <DialogTitle>Create Ticket</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1, minWidth: 320 }}>
            <TextField label="Booking" value={createBooking ? (createBooking.bookingDetails?.bookerName || createBooking.bookingDetails?.companyDetails?.name) : ''} disabled />
            <TextField label="Ticket Note (optional)" onChange={() => {}} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCreateModal}>Cancel</Button>
          <Button variant="contained" onClick={submitCreateTicket} disabled={processing}>Create</Button>
        </DialogActions>
      </Dialog>
      {/* Booker Detail Modal */}
      <BookerDetailModal open={detailOpen} onClose={() => setDetailOpen(false)} booker={detailBooker} />
      </Box>
    </StaffLayout>
  );
}

export default TicketSupport;
